import { useQuery } from "react-query";

import { getWellDetailCard } from "api/data";

import { Uwi } from "models";
import { EntityKind } from "models/entityKind";

import { SelectedWellT } from "components/well-list/context/WellListContext";

const TYPE_LOG_GROUP = "Type Log";
const TYPE_LOG_FIELDS = ["Geology.TypeLog"];

export async function fetchTypeLogDocument(selectedWell: Maybe<SelectedWellT>) {
  const response = await getWellDetailCard({
    uwid: selectedWell?.Uwi,
    group: TYPE_LOG_GROUP,
    fields: [...new Set(TYPE_LOG_FIELDS)],
    entityKind: EntityKind.Well
  });
  return response.data;
}

export function useTypeLogDocumentQuery(selectedWell: Maybe<SelectedWellT>) {
  const { data, isLoading, isError, error } = useQuery(
    ["typeLogDocument", selectedWell?.Uwi],
    () => fetchTypeLogDocument(selectedWell),
    {
      enabled: !!selectedWell?.Uwi,
      // retry: false matches the old useMutation-based fetch, which never
      // auto-retried on 401/404/generic errors (see useWellDetailGroupQuery.ts).
      retry: false,
      // keepPreviousData: the query key includes selectedWell?.Uwi, so
      // switching wells would otherwise reset `data` to undefined until the
      // new document resolves — blanking the pane (and worse: TypeLogWidget's
      // effect would build a Blob from `undefined`). The old TypeLogContext
      // only overwrote `state.document` in onSuccess, so the previous well's
      // document stayed visible while the next one loaded; this restores
      // that behavior (code review).
      keepPreviousData: true,
      // Log once per failure here (matches the old mutation's onError) rather
      // than in deriveTypeLogErrorMessage, which runs in the render path and
      // would otherwise re-log on every re-render while the error persists.
      onError: (err: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response = (err as any)?.response;
        if (response?.status !== 401 && response?.status !== 404 && response?.data) {
          // eslint-disable-next-line no-console
          console.error(response.data);
        }
      }
    }
  );

  return { data, isLoading, isError, error };
}

/**
 * Re-derives the user-facing error message that `TypeLogContext`'s
 * `typeLogErrorMessage` field used to store, from `useTypeLogDocumentQuery`'s
 * error state (Standing Rule 6 — derive, don't duplicate; STATE_OWNERSHIP_
 * CONTRACT.md §3.1/§3.2/§12.6).
 *
 * The old reducer-based implementation always fired the document fetch —
 * even with no well selected — and relied on the backend responding 404 for
 * an undefined `uwid` to surface the "please select a well" message. Since
 * `useTypeLogDocumentQuery` is gated with `enabled: !!selectedWell?.Uwi`
 * (never firing the request when nothing is selected), that case is now
 * derived directly from `selectedWell` instead of from a query error —
 * producing the identical message without the wasted network call.
 */
export function deriveTypeLogErrorMessage(
  selectedWell: Maybe<SelectedWellT>,
  documentQuery: Pick<ReturnType<typeof useTypeLogDocumentQuery>, "isError" | "error">
): Nullable<string> {
  if (!selectedWell?.Uwi) {
    return "Please select a well to view type logs.";
  }

  if (!documentQuery.isError) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = documentQuery.error as any;
  if (!err) {
    return null;
  }

  if (err.response?.status === 401) {
    return "Requires subscription.";
  }

  if (err.response?.status === 404) {
    return "No type log available.";
  }

  // Generic-error logging happens once per failure in the query's onError
  // (see useTypeLogDocumentQuery above) — this derive helper stays pure.
  return `Error loading details for well ${new Uwi().toFormatted(selectedWell.Uwi)}.`;
}
